// Play only the visible project demo. Native controls always remain available.
function mountResearchMedia() {
    const abort = new AbortController(), observers = [];
    let disposed = false;
    const listen = (target, type, callback) => target.addEventListener(type, callback, {signal: abort.signal});
    const cleanup = () => {
        disposed = true; abort.abort(); observers.forEach(observer => observer.disconnect());
        document.querySelectorAll('.research-video video').forEach(video => video.pause());
        document.querySelector('.moodboard-lightbox')?.close?.();
        document.documentElement.classList.remove('moodboard-lightbox-open');
    };
    document.querySelectorAll('[data-moodboard-demo]').forEach(demo => {
        const choices = [...demo.querySelectorAll('[data-moodboard-choice]')];
        const panels = [...demo.querySelectorAll('[data-moodboard-panel]')];
        const ready = new Map();
        let visible = choices.find(choice => choice.getAttribute('aria-pressed') === 'true').dataset.moodboardChoice;
        let revision = 0;

        function prepare(panel) {
            if (!ready.has(panel)) {
                const images = [...panel.querySelectorAll('img')];
                images.forEach(image => { image.loading = 'eager'; });
                ready.set(panel, Promise.allSettled(images.map(image => image.decode?.())));
            }
            return ready.get(panel);
        }

        function show(key) {
            const request = ++revision;
            if (key === visible) return;
            const next = panels.find(panel => panel.dataset.moodboardPanel === key);
            // Keep the current board on screen until the new images have decoded.
            prepare(next).then(() => {
                if (request !== revision) return;
                visible = key;
                choices.forEach(choice => choice.setAttribute('aria-pressed', String(choice.dataset.moodboardChoice === key)));
                panels.forEach(panel => { panel.hidden = panel !== next; });
            });
        }

        const warmImages = () => {
            demo.querySelectorAll('.moodboard-cluster img').forEach(image => { image.loading = 'eager'; });
            panels.forEach(prepare);
        };
        if ('IntersectionObserver' in window) {
            const preload = new IntersectionObserver(entries => {
                if (!entries.some(entry => entry.isIntersecting)) return;
                warmImages();
                preload.disconnect();
            }, { rootMargin: '240px' });
            observers.push(preload);
            preload.observe(demo);
        }

        demo.querySelector('.moodboard-choices').hidden = false;
        demo.dataset.moodboardReady = 'true';
        demo.addEventListener('pointerenter', event => {
            if (event.pointerType === 'touch') return;
            warmImages();
        });
        demo.addEventListener('focusin', warmImages);
        demo.addEventListener('click', event => {
            // Cancel any pending image swap while opening the visible board's lightbox.
            if (event.target.closest('[data-moodboard-image]')) show(visible);
        }, true);
        choices.forEach(button => {
            button.addEventListener('pointerenter', event => {
                if (event.pointerType !== 'touch') show(button.dataset.moodboardChoice);
            });
            button.addEventListener('focus', () => show(button.dataset.moodboardChoice));
            button.addEventListener('click', () => show(button.dataset.moodboardChoice));
        });
    });

    const lightbox = document.querySelector('.moodboard-lightbox');
    if (lightbox && typeof lightbox.showModal === 'function') {
        const image = lightbox.querySelector('.moodboard-lightbox-image');
        const caption = lightbox.querySelector('figcaption');
        let opener;

        document.querySelectorAll('[data-moodboard-image]').forEach(link => {
            // Parcel resolves this prefix at build time; keep direct file previews usable too.
            const assetHref = link.getAttribute('href');
            if (assetHref?.startsWith('url:')) link.setAttribute('href', assetHref.slice(4));
            link.setAttribute('aria-haspopup', 'dialog');
            link.addEventListener('click', event => {
                // Preserve the normal image link for new-tab clicks and no-JS use.
                if (event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
                event.preventDefault();
                opener = link;
                image.src = link.href;
                image.alt = link.querySelector('img').alt;
                caption.textContent = link.dataset.imageCaption;
                lightbox.showModal();
                document.documentElement.classList.add('moodboard-lightbox-open');
            });
        });
        lightbox.querySelector('.moodboard-lightbox-close').addEventListener('click', () => lightbox.close());
        lightbox.addEventListener('click', event => {
            if (event.target !== lightbox) return;
            const rect = lightbox.getBoundingClientRect();
            if (event.clientX < rect.left || event.clientX > rect.right ||
                event.clientY < rect.top || event.clientY > rect.bottom) lightbox.close();
        });
        lightbox.addEventListener('close', () => {
            document.documentElement.classList.remove('moodboard-lightbox-open');
            opener?.focus({ preventScroll: true });
        });
        listen(window, 'beforeprint', () => {
            if (lightbox.open) lightbox.close();
        });
    }

    document.querySelectorAll('[data-image-comparison]').forEach(comparison => {
        const slider = comparison.querySelector('input[type="range"]');
        const update = () => {
            const amount = Number(slider.value);
            comparison.style.setProperty('--reveal', `${amount}%`);
            slider.setAttribute('aria-valuetext', `${amount}% original, ${100 - amount}% Krea 3 output`);
        };
        slider.hidden = false;
        slider.addEventListener('input', update);
        update();
    });

    document.querySelectorAll('[data-regional-example]').forEach(example => {
        const button = example.querySelector('.regional-toggle');
        const patch = example.querySelector('.regional-patch');
        const hint = example.querySelector('[data-regional-hint]');
        button.hidden = false;
        button.addEventListener('click', () => {
            const edited = button.getAttribute('aria-pressed') !== 'true';
            button.setAttribute('aria-pressed', String(edited));
            patch.hidden = !edited;
            hint.textContent = edited ? 'Click the box to show original' : 'Click the box to show edit';
        });
    });

    const printDetails = new Set();
    listen(window, 'beforeprint', () => {
        document.querySelectorAll('.project-details:not([open])').forEach(detail => {
            printDetails.add(detail);
            detail.open = true;
        });
    });
    listen(window, 'afterprint', () => {
        printDetails.forEach(detail => { detail.open = false; });
        printDetails.clear();
    });

    const containers = [...document.querySelectorAll('.research-video')];
    if (!containers.length) return cleanup;
    const reducedMotion = matchMedia('(prefers-reduced-motion: reduce)');
    const darkMode = matchMedia('(prefers-color-scheme: dark)');
    const visible = new Set();
    const userPaused = new WeakSet();
    const automaticPause = new WeakSet();
    let printing = false;

    function pause(video) {
        if (!video.paused) {
            automaticPause.add(video);
            video.pause();
        }
    }

    function update(container) {
        container.querySelectorAll('video').forEach(video => {
            const active = !disposed && visible.has(container) && !document.hidden && !printing &&
                getComputedStyle(video).display !== 'none';
            if (active && video.preload === 'none') {
                video.preload = 'metadata';
                video.load();
            }
            if (!active) pause(video);
            else if (!reducedMotion.matches && !userPaused.has(container) && video.paused) {
                video.play().catch(() => {});
            }
        });
    }
    const updateAll = () => containers.forEach(update);
    const observer = new IntersectionObserver(entries => {
        entries.forEach(entry => {
            if (entry.isIntersecting && entry.intersectionRatio >= 0.5) visible.add(entry.target);
            else visible.delete(entry.target);
            update(entry.target);
        });
    }, { threshold: 0.5 });

    observers.push(observer);
    containers.forEach(container => {
        container.querySelectorAll('video').forEach(video => {
            video.addEventListener('pause', () => {
                if (automaticPause.has(video)) automaticPause.delete(video);
                else userPaused.add(container);
            });
            video.addEventListener('play', () => userPaused.delete(container));
        });
        observer.observe(container);
    });
    listen(darkMode, 'change', updateAll);
    listen(document, 'visibilitychange', updateAll);
    listen(reducedMotion, 'change', () => {
        if (reducedMotion.matches) containers.forEach(container => container.querySelectorAll('video').forEach(pause));
        updateAll();
    });
    listen(window, 'beforeprint', () => { printing = true; updateAll(); });
    listen(window, 'afterprint', () => { printing = false; updateAll(); });
    return cleanup;
}
let disposeResearchMedia = mountResearchMedia();
document.addEventListener('research:before-swap', () => disposeResearchMedia());
document.addEventListener('research:after-swap', () => { disposeResearchMedia = mountResearchMedia(); });
